import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import { Divider } from 'primereact/divider';
import { ProgressBar } from 'primereact/progressbar';
import storageUsageService from '../../services/storageUsageService';

const CHART_COLORS = ['#3B82F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#6366F1', '#14B8A6', '#F97316'];

const getSectionLabel = (section) => {
    const map = {
        leads: 'Leads', reports: 'Reports', profile: 'Profile Documents',
        bank_statements: 'Bank Statements', profiles: 'Profile Photos', feed: 'Social Feed',
        marketplace: 'Marketplace', conversions: 'Conversions', messaging: 'Messaging',
        establishment: 'Establishment'
    };
    return map[section] || section;
};

const getSectionIcon = (section) => {
    const map = {
        leads: 'pi-users', reports: 'pi-file', profile: 'pi-id-card',
        bank_statements: 'pi-wallet', profiles: 'pi-image', feed: 'pi-comments',
        marketplace: 'pi-shopping-cart', conversions: 'pi-sync', messaging: 'pi-envelope',
        establishment: 'pi-building'
    };
    return map[section] || 'pi-folder';
};

const MyStorageUsage = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [quota, setQuota] = useState(null);

    const toast = useRef(null);

    // Chart options — no built-in legend, we render our own
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '68%',
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return ` ${storageUsageService.formatBytes(context.raw)}`;
                    }
                }
            }
        }
    };

    const loadUsage = async () => {
        setLoading(true);
        setError(null);
        try {
            // Usage breakdown and plan quota load together; quota is best-effort
            // (a failure there must not blank out the usage view).
            const [usageRes, quotaRes] = await Promise.allSettled([
                storageUsageService.getMyUsage(),
                storageUsageService.getQuota(),
            ]);

            if (usageRes.status === 'fulfilled' && usageRes.value.success) {
                setData(usageRes.value.data);
            } else {
                setError('Failed to load storage usage data.');
            }

            if (quotaRes.status === 'fulfilled' && quotaRes.value.success) {
                setQuota(quotaRes.value.data);
            }
        } catch (err) {
            console.error('Error loading storage usage:', err?.message || 'Unknown error');
            setError('Failed to load storage usage data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsage();
    }, []);

    // Build chart data
    const getChartData = () => {
        const sections = data?.by_section;
        if (!sections || sections.length === 0) return null;
        return {
            labels: sections.map(s => getSectionLabel(s.section)),
            datasets: [{
                data: sections.map(s => s.bytes),
                backgroundColor: CHART_COLORS.slice(0, sections.length),
                borderWidth: 0,
                hoverOffset: 6
            }]
        };
    };

    const chartData = getChartData();
    const sections = data?.by_section || [];
    const totalBytes = data?.total_bytes || 0;
    const maxBytes = sections.length > 0 ? Math.max(...sections.map(s => s.bytes), 1) : 1;

    return (
        <div className="grid">
            <div className="col-12">
                <Card>
                    {/* Page Header */}
                    <div className="flex justify-content-between align-items-center mb-3">
                        <div>
                            <h1 className="text-2xl font-bold text-primary m-0">My Storage Usage</h1>
                            <p className="text-sm text-500 mt-1 mb-0">View your file storage usage across all sections</p>
                        </div>
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-outlined p-button-sm"
                            onClick={loadUsage}
                            loading={loading}
                            tooltip="Refresh"
                            tooltipOptions={{ position: 'bottom' }}
                            aria-label="Refresh"
                        />
                    </div>

                    <Divider className="my-2" />

                    {/* Error */}
                    {error && <Message severity="error" text={error} className="w-full mb-3" />}

                    {/* ── Plan Storage Quota (shared company pool) ── */}
                    {!loading && quota && !quota.unlimited && quota.limit_bytes && (() => {
                        const pct = Math.min(100, Math.round(quota.percent));
                        const barColor = pct >= 100
                            ? 'var(--red-500)'
                            : (quota.warn ? 'var(--orange-500)' : 'var(--green-500)');
                        return (
                            <div className="card mb-3 p-3 border-1 surface-border">
                                <div className="flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                                    <span className="text-900 font-semibold text-sm flex align-items-center gap-2">
                                        <i className="pi pi-server text-primary" />
                                        Plan Storage
                                    </span>
                                    <span className="text-sm text-700">
                                        <span className="font-semibold text-900">{quota.used_formatted}</span>
                                        {' of '}
                                        <span className="font-semibold text-900">{quota.limit_formatted}</span>
                                        <span className="text-500 ml-2">({pct}%)</span>
                                    </span>
                                </div>
                                <ProgressBar
                                    value={pct}
                                    showValue={false}
                                    style={{ height: '8px' }}
                                    color={barColor}
                                    aria-label="Plan storage used"
                                />
                                {pct >= 100 ? (
                                    <Message
                                        severity="error"
                                        text="Storage is full. Delete files or ask your admin to upgrade the plan to upload more."
                                        className="w-full mt-2"
                                    />
                                ) : quota.warn ? (
                                    <Message
                                        severity="warn"
                                        text={`You've used over ${quota.warn_threshold_percent}% of your storage. Consider freeing up space or upgrading soon.`}
                                        className="w-full mt-2"
                                    />
                                ) : null}
                            </div>
                        );
                    })()}

                    {/* ── Summary Cards (compact — 3 cards for user) ── */}
                    <div className="grid mb-3">
                        <div className="col-6 lg:col-4">
                            <div className="card mb-0 p-3 bg-blue-50 border-1 border-blue-200 h-full">
                                <div className="flex justify-content-between align-items-start">
                                    <div>
                                        <span className="block text-500 font-medium text-xs mb-2">Total Storage Used</span>
                                        <div className="text-900 font-bold text-lg">
                                            {loading ? <Skeleton width="4rem" height="1.2rem" /> : (data?.total_formatted || '0 B')}
                                        </div>
                                    </div>
                                    <div className="flex align-items-center justify-content-center bg-blue-100 border-round" style={{ width: '2rem', height: '2rem' }}>
                                        <i className="pi pi-database text-blue-500" style={{ fontSize: '0.85rem' }}></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-6 lg:col-4">
                            <div className="card mb-0 p-3 bg-green-50 border-1 border-green-200 h-full">
                                <div className="flex justify-content-between align-items-start">
                                    <div>
                                        <span className="block text-500 font-medium text-xs mb-2">Total Files</span>
                                        <div className="text-900 font-bold text-lg">
                                            {loading ? <Skeleton width="3rem" height="1.2rem" /> : (data?.total_files?.toLocaleString() || '0')}
                                        </div>
                                    </div>
                                    <div className="flex align-items-center justify-content-center bg-green-100 border-round" style={{ width: '2rem', height: '2rem' }}>
                                        <i className="pi pi-file text-green-500" style={{ fontSize: '0.85rem' }}></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 lg:col-4">
                            <div className="card mb-0 p-3 bg-cyan-50 border-1 border-cyan-200 h-full">
                                <div className="flex justify-content-between align-items-start">
                                    <div>
                                        <span className="block text-500 font-medium text-xs mb-2">Sections Used</span>
                                        <div className="text-900 font-bold text-lg">
                                            {loading ? <Skeleton width="2rem" height="1.2rem" /> : sections.length}
                                        </div>
                                    </div>
                                    <div className="flex align-items-center justify-content-center bg-cyan-100 border-round" style={{ width: '2rem', height: '2rem' }}>
                                        <i className="pi pi-th-large text-cyan-500" style={{ fontSize: '0.85rem' }}></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Empty State */}
                    {!loading && !error && data && data.total_files === 0 && (
                        <div className="text-center p-5">
                            <i className="pi pi-inbox text-400 mb-3" style={{ fontSize: '3rem' }}></i>
                            <p className="text-sm text-500 m-0">You haven't uploaded any files yet.</p>
                        </div>
                    )}

                    {/* ── Storage Distribution Chart + Section Legend ── */}
                    {!loading && chartData && sections.length > 0 && (
                        <div className="card mb-3 p-3 border-1 surface-border">
                            <div className="flex align-items-center mb-2">
                                <span className="text-900 font-semibold text-sm">Storage Distribution</span>
                            </div>
                            <div className="flex flex-column md:flex-row align-items-center md:align-items-start gap-3">
                                {/* Compact doughnut chart with total in center */}
                                <div className="flex-shrink-0 flex justify-content-center" style={{ width: '150px' }}>
                                    <div style={{ position: 'relative', width: '150px', height: '150px' }}>
                                        <Chart type="doughnut" data={chartData} options={chartOptions} style={{ width: '150px', height: '150px' }} />
                                        <div style={{
                                            position: 'absolute', top: '50%', left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            textAlign: 'center', pointerEvents: 'none'
                                        }}>
                                            <div className="text-500" style={{ fontSize: '0.6rem' }}>TOTAL</div>
                                            <div className="text-900 font-bold" style={{ fontSize: '0.8rem' }}>
                                                {data?.total_formatted || '0 B'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Right-aligned legend — 2 columns */}
                                <div className="flex-grow-1 w-full" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                                    <div className="grid m-0">
                                        {sections.map((s, i) => {
                                            const pct = totalBytes > 0 ? ((s.bytes / totalBytes) * 100).toFixed(1) : '0.0';
                                            return (
                                                <div key={s.section} className="col-6 py-1 px-2">
                                                    <div className="flex align-items-center gap-2">
                                                        <span
                                                            className="border-round flex-shrink-0"
                                                            style={{
                                                                width: '8px', height: '8px',
                                                                backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                                                                display: 'inline-block'
                                                            }}
                                                        ></span>
                                                        <div className="flex flex-column" style={{ minWidth: 0 }}>
                                                            <span className="text-xs font-medium text-700 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                                {getSectionLabel(s.section)}
                                                            </span>
                                                            <span style={{ fontSize: '0.7rem' }} className="text-500">
                                                                {s.formatted} &middot; {pct}%
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Section Breakdown with progress bars ── */}
                    {!loading && sections.length > 0 && (
                        <div className="card p-3 border-1 surface-border">
                            <div className="text-xs font-semibold text-500 uppercase mb-3">Section Breakdown</div>
                            {sections.map((section, i) => {
                                const pct = totalBytes > 0 ? ((section.bytes / totalBytes) * 100).toFixed(1) : '0.0';
                                return (
                                    <div key={section.section} className="mb-3">
                                        <div className="flex align-items-center justify-content-between mb-1">
                                            <div className="flex align-items-center gap-2">
                                                <span
                                                    className="border-round flex-shrink-0"
                                                    style={{
                                                        width: '8px', height: '8px',
                                                        backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                                                        display: 'inline-block'
                                                    }}
                                                ></span>
                                                <i className={`pi ${getSectionIcon(section.section)} text-500`} style={{ fontSize: '0.7rem' }}></i>
                                                <span className="text-sm font-medium text-900">{getSectionLabel(section.section)}</span>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-sm font-semibold text-primary">{section.formatted}</span>
                                                <span className="text-xs text-500 ml-2">{pct}%</span>
                                                <span className="text-xs text-400 ml-2">{section.files} files</span>
                                            </div>
                                        </div>
                                        <ProgressBar
                                            value={Math.round((section.bytes / maxBytes) * 100)}
                                            showValue={false}
                                            style={{ height: '4px' }}
                                            color={CHART_COLORS[i % CHART_COLORS.length]}
                                         aria-label="Progress" />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>
            </div>

            <Toast ref={toast} />
        </div>
    );
};

export default MyStorageUsage;
